import { useEffect, useRef } from 'react';

const TAU = Math.PI * 2;
const LEVELS = 12;
const POINTER_RADIUS = 150;
const PULSE_INTERVAL_MS = 650;
const MAX_PULSES = 7;
const MAX_RIPPLES = 4;
const RIPPLE_SPEED = 520;
const RIPPLE_LIFE_MS = 1500;
const TONES = ['glow', 'pulse', 'ripple'];

function readPalette() {
  const styles = getComputedStyle(document.documentElement);
  const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  return {
    dark,
    base: read('--color-text', dark ? '#eef0f7' : '#0c0e16'),
    glow: read('--color-accent-text', '#a9acff'),
    pulse: read('--color-accent-2', '#22d3ee'),
    ripple: read('--color-accent-3', '#c084fc'),
  };
}

function buildGrid(width, height) {
  const spacing = width < 720 ? 22 : 28;
  const columns = Math.ceil(width / spacing) + 1;
  const rows = Math.ceil(height / spacing) + 1;
  const offsetX = (width - (columns - 1) * spacing) / 2;
  const offsetY = (height - (rows - 1) * spacing) / 2;
  const count = columns * rows;
  const xs = new Float32Array(count);
  const ys = new Float32Array(count);
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const index = row * columns + column;
      xs[index] = offsetX + column * spacing;
      ys[index] = offsetY + row * spacing;
    }
  }
  return { spacing, columns, rows, count, xs, ys, boost: new Float32Array(count), tone: new Uint8Array(count) };
}

function spawnPulse(grid) {
  const horizontal = Math.random() < 0.6;
  const length = horizontal ? grid.columns : grid.rows;
  const forward = Math.random() < 0.5;
  return {
    horizontal,
    line: Math.floor(Math.random() * (horizontal ? grid.rows : grid.columns)),
    head: forward ? -4 : length + 4,
    direction: forward ? 1 : -1,
    speed: 9 + Math.random() * 9,
    tail: 6 + Math.floor(Math.random() * 8),
    limit: length + 16,
  };
}

export function AuthScene() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let palette = readPalette();
    let grid = null;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame = 0;
    let last = performance.now();
    let elapsed = Math.random() * 100;
    let pulseTimer = 0;
    let pulses = [];
    let ripples = [];
    const pointer = { x: -9999, y: -9999, strength: 0, target: 0 };

    const resize = () => {
      const nextWidth = canvas.clientWidth;
      const nextHeight = canvas.clientHeight;
      const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
      if (nextWidth === width && nextHeight === height && nextDpr === dpr && grid) return false;
      width = nextWidth;
      height = nextHeight;
      dpr = nextDpr;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      grid = buildGrid(width, height);
      pulses = [];
      return true;
    };

    const applyPulses = (delta) => {
      grid.boost.fill(0);
      grid.tone.fill(0);
      pulses = pulses.filter((pulse) => {
        pulse.head += pulse.direction * pulse.speed * delta;
        const head = Math.round(pulse.head);
        for (let step = 0; step < pulse.tail; step += 1) {
          const cell = head - step * pulse.direction;
          const length = pulse.horizontal ? grid.columns : grid.rows;
          if (cell < 0 || cell >= length) continue;
          const index = pulse.horizontal ? pulse.line * grid.columns + cell : cell * grid.columns + pulse.line;
          const value = (1 - step / pulse.tail) ** 1.6;
          if (value > grid.boost[index]) {
            grid.boost[index] = value;
            grid.tone[index] = 2;
          }
        }
        return pulse.direction > 0 ? pulse.head < pulse.limit : pulse.head > -16;
      });
    };

    const draw = (time) => {
      resize();
      const { count, xs, ys, boost, tone, spacing } = grid;
      const buckets = TONES.reduce(
        (map, key) => ({ ...map, [key]: Array.from({ length: LEVELS }, () => null) }),
        { base: Array.from({ length: LEVELS }, () => null) }
      );
      const baseAlpha = palette.dark ? 0.13 : 0.16;
      const waveScale = 1 / 240;
      const sigma = 2 * POINTER_RADIUS * POINTER_RADIUS;
      const now = performance.now();

      for (let index = 0; index < count; index += 1) {
        const x = xs[index];
        const y = ys[index];
        const u = x * waveScale;
        const v = y * waveScale;
        const wave =
          (Math.sin(u * 1.1 + time * 0.45) + Math.sin(v * 1.6 - time * 0.32) + Math.sin((u + v) * 0.7 + time * 0.22) + 3) / 6;
        let alpha = baseAlpha + wave ** 5 * 0.55;
        let radius = 0.9 + wave ** 4 * 0.9;
        let key = 'base';
        let strongest = 0;

        if (pointer.strength > 0.01) {
          const dx = x - pointer.x;
          const dy = y - pointer.y;
          const glow = Math.exp(-(dx * dx + dy * dy) / sigma) * pointer.strength;
          if (glow > 0.02) {
            alpha += glow * 0.85;
            radius += glow * 1.5;
            if (glow > 0.12) {
              key = 'glow';
              strongest = glow;
            }
          }
        }

        if (boost[index] > 0) {
          alpha += boost[index] * 0.9;
          radius += boost[index] * 1.2;
          if (boost[index] > strongest && tone[index] === 2) {
            key = 'pulse';
            strongest = boost[index];
          }
        }

        for (let r = 0; r < ripples.length; r += 1) {
          const ripple = ripples[r];
          const age = now - ripple.start;
          const ringRadius = (age / 1000) * RIPPLE_SPEED;
          const dx = x - ripple.x;
          const dy = y - ripple.y;
          const distance = Math.abs(Math.sqrt(dx * dx + dy * dy) - ringRadius);
          if (distance < spacing * 1.6) {
            const fade = 1 - age / RIPPLE_LIFE_MS;
            const value = (1 - distance / (spacing * 1.6)) * fade;
            alpha += value * 0.9;
            radius += value * 1.4;
            if (value > strongest) {
              key = 'ripple';
              strongest = value;
            }
          }
        }

        if (alpha < 0.03) continue;
        const level = Math.min(LEVELS - 1, Math.floor(Math.min(alpha, 1) * LEVELS));
        const bucket = buckets[key];
        if (!bucket[level]) bucket[level] = new Path2D();
        const path = bucket[level];
        path.moveTo(x + radius, y);
        path.arc(x, y, radius, 0, TAU);
      }

      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);
      for (const [key, bucket] of Object.entries(buckets)) {
        context.fillStyle = palette[key];
        bucket.forEach((path, level) => {
          if (!path) return;
          context.globalAlpha = (level + 1) / LEVELS;
          context.fill(path);
        });
      }
      context.globalAlpha = 1;
    };

    const tick = (now) => {
      const delta = Math.min(0.05, (now - last) / 1000);
      last = now;
      elapsed += delta;
      pointer.strength += (pointer.target - pointer.strength) * Math.min(1, delta * 6);
      pulseTimer += delta * 1000;
      if (resize()) pulseTimer = 0;
      if (pulseTimer > PULSE_INTERVAL_MS && pulses.length < MAX_PULSES) {
        pulseTimer = 0;
        pulses.push(spawnPulse(grid));
      }
      ripples = ripples.filter((ripple) => now - ripple.start < RIPPLE_LIFE_MS);
      applyPulses(delta);
      draw(elapsed);
      frame = requestAnimationFrame(tick);
    };

    const onPointerMove = (event) => {
      if (event.pointerType === 'touch') return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.target = 1;
    };
    const onPointerLeave = () => {
      pointer.target = 0;
    };
    const onPointerDown = (event) => {
      if (ripples.length >= MAX_RIPPLES) ripples.shift();
      ripples.push({ x: event.clientX, y: event.clientY, start: performance.now() });
    };

    const themeObserver = new MutationObserver(() => {
      palette = readPalette();
      if (reduced) draw(elapsed);
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const paletteTimer = setTimeout(() => {
      palette = readPalette();
      if (reduced) draw(elapsed);
    }, 1200);

    resize();
    if (reduced) {
      draw(elapsed);
      const onResize = () => draw(elapsed);
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('resize', onResize);
        themeObserver.disconnect();
        clearTimeout(paletteTimer);
      };
    }

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    document.documentElement.addEventListener('pointerleave', onPointerLeave);
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      themeObserver.disconnect();
      clearTimeout(paletteTimer);
    };
  }, []);

  return (
    <div className="auth-scene" aria-hidden="true">
      <span className="auth-scene__aurora auth-scene__aurora--one" />
      <span className="auth-scene__aurora auth-scene__aurora--two" />
      <span className="auth-scene__aurora auth-scene__aurora--three" />
      <canvas ref={canvasRef} className="auth-scene__canvas" />
    </div>
  );
}
